/**
 * calendarMetricScale.ts
 *
 * The shared five-step ramp every calendar page uses to say how a (room kind,
 * date) is doing. The Pricing Calendar tints by demand; the Price Drafts
 * calendar reuses the ramp for market pressure and comp-set position; the
 * market heat strip reuses it for the demand index — so one key reads the same
 * way everywhere.
 *
 * The ramp is a verdict, not a magnitude: it runs **red at step 1 to green at
 * step 5**, where red means "this date needs you" and green means "this date is
 * selling itself". Minimal demand is the alarming end; high demand is the good
 * one. Any metric whose *high* end is the bad one — market pressure is the only
 * one today — has to enter the ramp through {@link reverseDemandLevel} rather
 * than being tinted straight, or the worst dates come out green.
 *
 * The `--demand-1` … `--demand-5` tokens carry one shared red → green ramp in
 * every hued skin, so switching skins never moves which end is the bad one. The
 * high-contrast skin is the deliberate exception: it has no hue to spend
 * (red/green is the exact pairing its readers cannot resolve), so it says the
 * same thing in ink, heaviest at step 1 and fading to nothing by step 5. That
 * is why the ramp is safe as a background but never the only carrier of a
 * value — the cell always spells the number out as text too.
 */

import type { DemandLevel } from "../types/api";

/** Ordered low → high demand, i.e. worst → best. Drives the legend and every bucket function. */
export const DEMAND_LEVELS_ORDERED: DemandLevel[] = [
    "minimal",
    "low",
    "standard",
    "elevated",
    "high",
];

/**
 * Full-strength ramp, for cells whose value lives outside the tinted box
 * (the Pricing Calendar puts its price in a nested element).
 */
export const DEMAND_TINT_CLASS: Record<DemandLevel, string> = {
    minimal: "bg-demand-1",
    low: "bg-demand-2",
    standard: "bg-demand-3",
    elevated: "bg-demand-4",
    high: "bg-demand-5",
};

/**
 * Softened ramp, for cells that render text directly on the tint. At 30% over
 * `--card` every step of every skin clears 4.5:1 against `--foreground`, pinned
 * by `theme/contrast.test.ts`.
 *
 * Written out literally: Tailwind's scanner cannot see a composed
 * `bg-demand-${n}/30`, and the rule would be dropped from the build.
 */
export const DEMAND_TINT_SOFT_CLASS: Record<DemandLevel, string> = {
    minimal: "bg-demand-1/30",
    low: "bg-demand-2/30",
    standard: "bg-demand-3/30",
    elevated: "bg-demand-4/30",
    high: "bg-demand-5/30",
};

/**
 * The step diametrically opposite `level` on the ramp — step 1 ↔ 5, 2 ↔ 4, 3
 * fixed. Its own inverse.
 *
 * For the metrics that run the wrong way round. The ramp says "red needs you,
 * green is fine", so a metric measuring trouble rather than health has to be
 * flipped before it is tinted. Market pressure is pressure to discount: it
 * peaks on the dates selling worst, and tinting it straight would paint exactly
 * those dates green.
 */
export function reverseDemandLevel(level: DemandLevel): DemandLevel {
    const index = DEMAND_LEVELS_ORDERED.indexOf(level);
    return DEMAND_LEVELS_ORDERED[DEMAND_LEVELS_ORDERED.length - 1 - index];
}

/**
 * One swatch size for every calendar legend key, on every calendar page.
 *
 * The Price Drafts toolbar alone carries three keys — demand, pickup and the
 * two-ended metric ramp — and each had hard-coded its own size, so keys sat
 * side by side at 12 px and 16 px while the market heat strip used a third
 * value. Naming the size once is what keeps them from drifting apart again.
 *
 * A literal class, not a composed one, so Tailwind's scanner emits the rule.
 */
export const LEGEND_SWATCH_SIZE_CLASS = "size-4.5";

/**
 * Booking pace (actual OTB / expected OTB at this lead time) → ramp step.
 *
 * Thresholds mirror `DEMAND_THRESHOLDS` in
 * `optivo-service/src/services/calendar/calendarDemand.ts`, which is the source
 * of truth — there is no shared package between the two, so the constants are
 * duplicated here and pinned by test. Ratios are unbounded above (live snapshots
 * reach ~1389 when expected OTB is near zero), so the top bucket saturates.
 */
export function paceRatioToDemandLevel(paceRatio: number): DemandLevel | null {
    if (!Number.isFinite(paceRatio)) return null;
    if (paceRatio >= 1.35) return "high";
    if (paceRatio >= 1.15) return "elevated";
    if (paceRatio >= 0.95) return "standard";
    if (paceRatio >= 0.7) return "low";
    return "minimal";
}

/**
 * Occupancy percentage (0-100) → ramp step.
 *
 * Five buckets to match the ramp, deliberately not the six-level
 * `PRICING_LEVELS` scale in the backend's `computations.ts` — that one drives
 * MinLOS decisions, not a visual heat cue.
 *
 * Reads correctly against the ramp as-is: a full date is a good date. The
 * Price Drafts occupancy view still tints by pickup verdict instead (see
 * `drafts/occupancyPickup.ts`), because "empty" only becomes bad once the
 * booking window is open, and raw occupancy cannot tell you that.
 */
export function occupancyPctToLevel(pct: number): DemandLevel {
    if (pct >= 85) return "high";
    if (pct >= 70) return "elevated";
    if (pct >= 50) return "standard";
    if (pct >= 30) return "low";
    return "minimal";
}

/**
 * Market pressure percentage (0-100) → ramp step, in even 20-point bands.
 *
 * Returns the step for the *magnitude* of the pressure, which is not the step
 * it should be tinted with — high pressure is a bad date, and the ramp's high
 * end is the good one. Callers pass the result through
 * {@link reverseDemandLevel}; `resolveCellMetric` is the one that does.
 */
export function pressurePctToLevel(pct: number): DemandLevel {
    if (pct >= 80) return "high";
    if (pct >= 60) return "elevated";
    if (pct >= 40) return "standard";
    if (pct >= 20) return "low";
    return "minimal";
}

/**
 * Market demand index (0..1, 0.5 neutral) → ramp step.
 *
 * Bands are deliberately narrower than `pressurePctToLevel`'s even 20-point
 * split. The backend squashes its raw shift through `tanh(2 × shift)`, which
 * compresses ordinary dates hard toward 0.5: a lone school holiday at weight
 * 0.35 only reaches 0.80, and a full-strength rate percentile at weight 0.2
 * only 0.69. Even bands would drop almost every real date into "standard" and
 * the ramp would carry no information. These thresholds correspond to shifts
 * of roughly ±0.08 and ±0.21.
 *
 * Returns null — like `paceRatioToDemandLevel`, unlike `occupancyPctToLevel` —
 * so a date with no signals renders untinted rather than as a claim.
 */
export function demandIndexToLevel(index: number): DemandLevel | null {
    if (!Number.isFinite(index)) return null;
    if (index >= 0.7) return "high";
    if (index >= 0.58) return "elevated";
    if (index >= 0.42) return "standard";
    if (index >= 0.3) return "low";
    return "minimal";
}

/**
 * Where the local comp set priced a night within its own captured range.
 *
 * Even 20-point bands, unlike `demandIndexToLevel`: this is a raw min-max
 * percentile with no tanh squash behind it, so the values genuinely spread
 * across the full 0-100 and narrower bands would misrepresent an even
 * distribution as a clustered one.
 *
 * Returns null for a non-finite input so a night the comp set never covered
 * renders untinted. An uncaptured night is an absence of evidence, and painting
 * it mid-ramp would state "average" about data nobody has.
 */
export function marketPercentileToLevel(pct: number): DemandLevel | null {
    if (!Number.isFinite(pct)) return null;
    if (pct >= 80) return "high";
    if (pct >= 60) return "elevated";
    if (pct >= 40) return "standard";
    if (pct >= 20) return "low";
    return "minimal";
}
